import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { resolveFileUrl } from '../lib/api';

export default function VideoPlayer({ source, poster, title }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current || !source) return;
    const el = videoRef.current;
    let hls;
    if (source.type === 'hls' && source.url) {
      if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = source.url;
      } else if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true });
        hls.loadSource(source.url);
        hls.attachMedia(el);
      }
    }
    return () => { if (hls) hls.destroy(); };
  }, [source]);

  if (!source) {
    return (
      <div className="w-full aspect-video rounded-xl bg-black flex items-center justify-center text-white/50">No source</div>
    );
  }

  if (source.type === 'youtube') {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${source.id}?rel=0&modestbranding=1`} title={title || 'Video player'} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen data-testid="player-youtube" />
      </div>
    );
  }

  if (source.type === 'vimeo') {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${source.id}`} title={title || 'Video player'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="player-vimeo" />
      </div>
    );
  }

  if (source.type === 'gdrive') {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={`https://drive.google.com/file/d/${source.id}/preview`} title={title || 'Video player'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="player-gdrive" />
      </div>
    );
  }

  if (source.type === 'dailymotion') {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={`https://www.dailymotion.com/embed/video/${source.id}`} title={title || 'Video player'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="player-dailymotion" />
      </div>
    );
  }

  if (source.type === 'twitch') {
    const parent = window.location.hostname;
    const src = source.kind === 'clip'
      ? `https://clips.twitch.tv/embed?clip=${source.id}&parent=${parent}`
      : `https://player.twitch.tv/?video=${source.id}&parent=${parent}`;
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={src} title={title || 'Video player'} allowFullScreen data-testid="player-twitch" />
      </div>
    );
  }

  if (source.type === 'facebook') {
    const enc = encodeURIComponent(source.url);
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
        <iframe className="w-full h-full" src={`https://www.facebook.com/plugins/video.php?href=${enc}&show_text=false&autoplay=false`} title={title || 'Video player'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="player-facebook" />
      </div>
    );
  }

  // HLS, direct, file — native <video> element (Range streaming)
  const src = source.type === 'hls' ? undefined
    : source.type === 'direct' ? source.url
    : resolveFileUrl(source.url);
  return (
    <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xl">
      <video ref={videoRef} controls playsInline crossOrigin="anonymous"
             controlsList="nodownload" disablePictureInPicture={false}
             poster={resolveFileUrl(poster)} className="w-full h-full"
             src={src} data-testid={`player-${source.type}`} />
    </div>
  );
}
